import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { object as YupObject, string as YupString } from 'yup';

import { useCurrentTheme } from 'app/shared/theme';
import { emailValidationRegex } from 'app/shared/utils/emailValidation';
import { EventAttendee, Ticket } from 'app/shared/typings';
import { UpdateTickets } from 'app/shared/graphql/tickets/mutationHooks';
import { Box, Typography } from 'app/shared/components/atoms';
import {
  AlternativesDivider,
  IconButton,
} from 'app/shared/components/molecules';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { Expandable } from 'app/fan/components/atoms';
import { GuestShareLink } from 'app/fan/components/organisms/GuestShareLink';
import { ReactComponent as Minus } from 'icons/collapse.svg';
import { ReactComponent as Plus } from 'icons/expand.svg';

import { FormContent } from './FormContent';

interface FormFields {
  [valueKey: string]: string | undefined;
}

interface AttendeeDetailsFormProps {
  ticketPurchaserName?: string;
  eventAttendee: EventAttendee;
}

export const AttendeeDetailsForm: React.FC<
  React.PropsWithChildren<AttendeeDetailsFormProps>
> = ({ ticketPurchaserName, eventAttendee }) => {
  const { event } = useEventPageContext();
  const [formDirty, setFormDirty] = useState(false);
  const { formatMessage } = useIntl();
  const updateTickets = UpdateTickets();
  const { colors } = useCurrentTheme();

  const [isFormExpanded, setIsFormExpanded] = useState<boolean>(true);
  function toggle() {
    setIsFormExpanded((isFormExpanded) => !isFormExpanded);
  }
  const togglerIcon = isFormExpanded ? Minus : Plus;

  const getValidationError = (
    tickets: Ticket[],
    setFieldError: (field: string, message: string | undefined) => void
  ): string | null => {
    const inputSchema = YupObject().shape({
      email: YupString().matches(emailValidationRegex, {
        message: formatMessage(
          {
            id: 'form.fieldMustBeValidEmail',
          },
          {
            field: 'Email',
          }
        ),
      }),
    });
    const ticketsInvalidEmail = tickets.filter(
      (ticket) => ticket.email && !inputSchema.isValidSync(ticket)
    );
    const ticketsNoEmail = tickets.filter(
      (ticket) => (ticket.firstName || ticket.lastName) && !ticket.email
    );

    const emailInvalid = formatMessage({
      id: 'event.attendeeDetailsForm.errorInvalidEmail',
    });
    const emailRequired = formatMessage({
      id: 'event.attendeeDetailsForm.errorRequired',
    });

    tickets.forEach((ticket) => {
      if (ticketsInvalidEmail.includes(ticket)) {
        setFieldError(`email-${ticket.id}`, emailInvalid);
      } else if (ticketsNoEmail.includes(ticket)) {
        setFieldError(`email-${ticket.id}`, emailRequired);
      }
    });

    if (ticketsInvalidEmail.length > 0) {
      return emailInvalid;
    } else if (ticketsNoEmail.length > 0) {
      return emailRequired;
    }

    return null;
  };

  async function handleSubmit(
    values: FormFields,
    { setSubmitting, setStatus, setFieldError }: FormikHelpers<FormFields>
  ) {
    const ticketsForGraphQL = tickets.map((ticket, index) => ({
      id: ticket.id,
      firstName: values[`firstName-${index}`],
      lastName: values[`lastName-${index}`],
      email: values[`email-${index}`],
    }));

    const validationError = getValidationError(
      ticketsForGraphQL,
      setFieldError
    );

    if (validationError) {
      setStatus({ success: false, error: validationError });
      setSubmitting(false);
    } else {
      setSubmitting(true);
      try {
        await updateTickets({
          variables: { tickets: ticketsForGraphQL },
        });

        setStatus({ success: true, error: undefined });
        setSubmitting(false);
        setFormDirty(false);
      } catch (e) {
        if (e instanceof Error) {
          setStatus({
            success: false,
            error: formatMessage({ id: 'event.attendeeDetailsForm.error' }),
          });
          setSubmitting(false);
          setFormDirty(false);
        }
      }
    }
  }

  if (!eventAttendee || !event) {
    return null;
  }

  const { tickets } = eventAttendee;
  const initialValues: FormFields = {};
  tickets.map((ticket, index) => {
    initialValues[`firstName-${index}`] = ticket.firstName;
    initialValues[`lastName-${index}`] = ticket.lastName;
    initialValues[`email-${index}`] = ticket.email;
  });

  return (
    <Box
      backgroundColor={colors.primary.black.light}
      alignItems="center"
      flexDirection="column"
      py={6}
      px={4}
      mx={0}
      mb={2}
      data-qaid="event-attendee-details-form"
    >
      <Box justifyContent="space-between" w="100%" backgroundColor="inherit">
        <Typography
          as="h2"
          fontWeight="medium"
          fontSize="fontSize500"
          display="block"
          fontFamily="title"
          color={colors.primary.white.base}
        >
          {formatMessage({ id: 'event.attendeeDetailsForm.title' })}
        </Typography>
        <IconButton
          icon={togglerIcon}
          onClick={toggle}
          size="lg"
          fillColor={colors.accent.primaryAccent.base}
          backgroundColor="inherit"
          strokeColor="none"
          p={2}
        />
      </Box>
      <Expandable
        isExpanded={isFormExpanded}
        transitionSpeed={500}
        collapsedHeight="0px"
        w="100%"
      >
        {tickets.length > 1 && (
          <GuestShareLink
            marginTop={6}
            buttonMarginTop={4}
            event={event}
            ticketPurchaserName={ticketPurchaserName}
            ticketPurchaserEventAttendeeId={eventAttendee?.id}
          />
        )}
        <Box pt={9} pb={5} justifyContent="center">
          <AlternativesDivider
            color={colors.accent.primaryAccent.base}
            textColor={colors.primary.white.base}
            backgroundColor={colors.primary.black.light}
            maxWidth="80%"
          />
        </Box>
        <Typography color={colors.primary.white.base} display="block" mb={6}>
          {formatMessage({ id: 'event.attendeeDetailsForm.instructions' })}
        </Typography>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {(formikProps) => (
            <FormContent
              {...formikProps}
              tickets={tickets}
              formDirty={formDirty}
              setFormDirty={setFormDirty}
            />
          )}
        </Formik>
      </Expandable>
    </Box>
  );
};
